import { useState } from "react";
import {
  Monitor,
  Play,
  Shield,
  Flag,
  Grid,
  Camera,
  Server,
  Smartphone,
  RotateCcw,
  Lock,
  MessageCircle,
  Gift,
  Diamond,
  Trophy,
  Tv,
  Zap,
  CheckCircle,
} from "lucide-react";
import apple from '../assets/logo/Apple_Pay.png'
import discover from '../assets/logo/Discover.png'
import cb from '../assets/logo/GIE_CB.png'
import Google from '../assets/logo/Google_Pay.png'
import mastercard from '../assets/logo/Mastercard.png'
import visa from '../assets/logo/Visa.png'
import ucl1 from '../assets/bg/img1.jpg'
import ucl2 from '../assets/bg/img2.jpg'
import ucl3 from '../assets/bg/img3.jpg'

import { CONTACT_CONFIG } from "../config/contact";

// --- Standard plans (€29 / €39 / €49) ---
const STANDARD_PLANS = [
  {
    id: "basic",
    name: "FORFAIT DE BASE",
    originalPrice: 39,
    currentPrice: 29,
    duration: "15 MOIS",
    bonus: "12 MOIS + 3 MOIS GRATUIT",
    screens: 1,
    link: "https://tvdiamo.store/order.php?id=1",
    variant: "clean",
    badgeLabel: null,
    showAdultLock: false,
    showDoubleScreen: false,
  },
  {
    id: "premium",
    name: "FORFAIT PREMIUM",
    originalPrice: 49,
    currentPrice: 39,
    duration: "24 MOIS",
    bonus: "2 ANS",
    screens: 1,
    link: "https://tvdiamo.store/order.php?id=2",
    variant: "premium",
    badgeLabel: "LE PLUS POPULAIRE",
    showAdultLock: true,
    showDoubleScreen: false,
  },
  {
    id: "gold",
    name: "FORFAIT GOLD",
    originalPrice: 69,
    currentPrice: 49,
    duration: "15 MOIS",
    bonus: "2 ÉCRANS",
    screens: 2,
    link: "https://tvdiamo.store/order.php?id=3",
    variant: "clean",
    badgeLabel: "MEILLEUR PRIX",
    showAdultLock: true,
    showDoubleScreen: true,
  },
];

// --- Multi-Screen plans (€69 / €79 / €89) ---
const MULTI_SCREEN_PLANS = [
  {
    id: "ms-2",
    name: "FORFAIT DIAMANT",
    originalPrice: 69,
    currentPrice: 59,
    duration: "2 ANS",
    bonus: "2 ÉCRANS",
    screens: 2,
    link: "https://tvdiamo.store/order.php?id=12",
    variant: "clean",
    badgeLabel: null,
  },
  {
    id: "ms-3",
    name: "FORFAIT PLATINUM",
    originalPrice: 79,
    currentPrice: 69,
    duration: "18 MOIS",
    bonus: "3 ÉCRANS",
    screens: 3,
    link: "https://tvdiamo.store/order.php?id=13",
    variant: "premium",
    badgeLabel: "LE PLUS POPULAIRE",
  },
  {
    id: "ms-4",
    name: "FORFAIT ROYALE",
    originalPrice: 89,
    currentPrice: 79,
    duration: "15 MOIS",
    bonus: "4 ÉCRANS",
    screens: 4,
    link: "https://tvdiamo.store/order.php?id=14",
    variant: "clean",
    badgeLabel: "MEILLEUR PRIX",
  },
];

const PRICING_DATA = {
  lifetime: {
    name: "FORFAIT A VIE",
    price: 129.0,
    features: [
      "Paiement unique",
      "2 écrans",
      "Accès complet au Forfait PREMIUM",
      "90 Jours Garantie De Remboursement"
    ],
    link: "https://tvdiamo.store/order.php?id=4"
  }
};

// Shared feature list for plan cards
const PLAN_FEATURES = [
  { icon: Monitor, text: "+79.000 Chaînes, +249.000 Films et Séries", color: "blue" },
  { icon: Play, text: "Netflix, Disney+, Amazon Prime Video, HBO Max, OCS, Paramount+, Apple+, Discovery+,", color: "orange" },
  { icon: Shield, text: "Ligue 1 Plus + France en FULL HD / 4K", color: "green" },
  { icon: Flag, text: "Choix illimité de Films & Séries", color: "pink" },
  { icon: Grid, text: "Qualité SD / HD / FULL HD / 4K", color: "purple" },
  { icon: Camera, text: "Catch-up et EPG", color: "blue" },
  { icon: Server, text: "Serveurs Rapides Et Stables", color: "blue" },
  { icon: Smartphone, text: "Compatible avec tous les appareils", color: "green" },
  { icon: RotateCcw, text: "Contenu régulièrement mis à jour", color: "orange" },
  { icon: Shield, text: "15 Jours Garantie De Remboursement", color: "purple" },
];

function PricingPlanCard({ plan, isStandard, formatPrice, createCheckoutUrl, onTrackAddToCart }) {
  const isPremium = plan.variant === "premium";
  const cardClass = isPremium
    ? "pricing-card pricing-card-premium"
    : "pricing-card pricing-card-clean group";
  const hasOriginalPrice = isStandard && plan.originalPrice != null;

  return (
    <div className={cardClass} id={plan.id}>
      {isPremium && (
        <>
          <div className="diamond" style={{ top: "10%", left: "5%", animationDelay: "0s" }}></div>
          <div className="diamond" style={{ top: "20%", right: "10%", animationDelay: "2s" }}></div>
          <div className="diamond" style={{ bottom: "30%", left: "8%", animationDelay: "4s" }}></div>
          <div className="star" style={{ top: "15%", right: "15%", animationDelay: "1s" }}></div>
          <div className="star" style={{ bottom: "20%", right: "5%", animationDelay: "3s" }}></div>
          <div className="star" style={{ top: "60%", left: "3%", animationDelay: "5s" }}></div>
        </>
      )}
      {plan.badgeLabel && (
        <div className="badge bg-blue-500 text-white rounded-full px-2 py-1 text-xs font-semibold">
          {plan.badgeLabel}
        </div>
      )}
      <div className={`text-center mb-6 sm:mb-8 mt-5 ${isPremium ? "" : ""}`}>
        <h3 className={`text-2xl font-black mb-3 sm:mb-4 transition-colors duration-500 ${isPremium ? "text-white mb-4" : "text-gray-900 group-hover:text-white"}`}>
          {plan.name}
        </h3>
        <div className="inline-block bg-gradient-to-r from-[#00d4ff] to-[#090979] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm font-bold mb-4 sm:mb-6">
          {plan.bonus}
        </div>
        <div className="mb-4 sm:mb-6">
            <span className={`line-through text-lg ${isPremium ? "text-blue-200" : "text-gray-400 group-hover:text-blue-200"} transition-colors duration-500`}>
              {formatPrice(plan.originalPrice)}
            </span>
        
          <div className={`text-4xl font-black transition-colors duration-500 ${isPremium ? "text-white" : "text-red-600 group-hover:text-white"}`}>
            {plan.currentPrice}
          </div>
          <span className={`text-sm transition-colors duration-500 ${isPremium ? "text-blue-200" : "text-gray-500 group-hover:text-blue-200"}`}>
            {plan.duration ? `€ / ${plan.duration}` : "€"}
          </span>
        </div>
      </div>
      {isStandard && (
        <div className="mb-4 sm:mb-6">
          <div className={`w-full bg-gradient-to-r from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-[#090979] font-bold py-2 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 sm:gap-3 border-2 border-blue-300 text-sm sm:text-base ${isPremium ? "from-blue-200 to-blue-300 hover:from-blue-300 hover:to-blue-400 border-blue-400" : "group-hover:from-blue-200 group-hover:to-blue-300 group-hover:border-blue-400"}`}>
            <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            <span>+ Gratuit IBO Pro Player!</span>
          </div>
        </div>
      )}
      <div className={`space-y-3 sm:space-y-4 mb-6 sm:mb-8 ${isPremium ? "space-y-4 mb-8" : ""}`}>
        {plan.showAdultLock && (
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="relative">
              <Lock className={`w-4 h-4 sm:w-5 sm:h-5 mt-1 flex-shrink-0 ${isPremium ? "text-pink-300" : "text-pink-500 group-hover:text-pink-300"} transition-colors duration-500`} />
              <div className="absolute -top-2 -right-2 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">18</div>
            </div>
            <span className={`text-xs sm:text-sm transition-colors duration-500 ${isPremium ? "text-white" : "text-gray-700 group-hover:text-white"}`}>
              Chaînes + Films Pour Adultes (avec mot de passe)
            </span>
          </div>
        )}
        {plan.showDoubleScreen && (
          <div className="flex items-start justify-center gap-3">
            <div className="flex items-center gap-2">
              <Monitor className={`w-5 h-5 transition-colors duration-500 ${isPremium ? "text-blue-300" : "text-blue-600 group-hover:text-blue-300"}`} />
              <Monitor className={`w-5 h-5 transition-colors duration-500 ${isPremium ? "text-blue-300" : "text-blue-600 group-hover:text-blue-300"}`} />
            </div>
            <span className={`text-sm transition-colors duration-500 ${isPremium ? "text-white" : "text-gray-700 group-hover:text-white"}`}>Deux écrans actifs</span>
          </div>
        )}
        {PLAN_FEATURES.map((f, i) => {
          const Icon = f.icon;
          const iconClassClean = { blue: "text-blue-600 group-hover:text-blue-300", orange: "text-orange-500 group-hover:text-orange-300", green: "text-green-500 group-hover:text-green-300", pink: "text-pink-500 group-hover:text-pink-300", purple: "text-purple-500 group-hover:text-purple-300" }[f.color];
          const iconClassPremium = { blue: "text-blue-300", orange: "text-orange-300", green: "text-green-300", pink: "text-pink-300", purple: "text-purple-300" }[f.color];
          return (
            <div key={i} className="flex items-start gap-2 sm:gap-3">
              <Icon className={`w-4 h-4 sm:w-5 sm:h-5 mt-1 flex-shrink-0 transition-colors duration-500 ${isPremium ? iconClassPremium : iconClassClean}`} />
              <span className={`text-xs sm:text-sm transition-colors duration-500 ${isPremium ? "text-white" : "text-gray-700 group-hover:text-white"}`}>
                {f.text}
              </span>
            </div>
          );
        })}
      </div>
      <button
        className="w-full bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] hover:brightness-110 hover:scale-105 text-white font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl transition-all duration-500 hover:shadow-lg text-sm sm:text-base min-h-[48px]"
        onClick={() => {
          onTrackAddToCart(plan.currentPrice);
          window.open(createCheckoutUrl(plan.id, plan.link), "_blank");
        }}
      >
        COMMANDER MAINTENANT
      </button>
      <div className="mt-6 sm:mt-8">
        <div className="relative flex items-center justify-center">
          <div className={`flex-1 h-px ${isPremium ? "bg-blue-200" : "bg-gray-300 group-hover:bg-gray-400 transition-colors duration-500"}`}></div>
          <div className="px-2">
            <p className={`text-xs sm:text-sm font-semibold whitespace-nowrap ${isPremium ? "text-white" : "text-gray-600 group-hover:text-white"}`}>Paiement Sécurisé Garanti</p>
          </div>
          <div className={`flex-1 h-px ${isPremium ? "bg-blue-200" : "bg-gray-300 group-hover:bg-white transition-colors duration-500"}`}></div>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mt-4 p-3 bg-white rounded-xl w-full">
          <img src={Google} alt="Google Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
          <img src={cb} alt="CB" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
          <img src={visa} alt="Visa" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
          <img src={apple} alt="Apple Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
          <img src={discover} alt="Discover" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
          <img src={mastercard} alt="Mastercard" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { whatsappUrl: WHATSAPP_URL } = CONTACT_CONFIG;
  const [planMode, setPlanMode] = useState("standard");
  const isStandard = planMode === "standard";
  const activePlans = isStandard ? STANDARD_PLANS : MULTI_SCREEN_PLANS;

  const createCheckoutUrl = (plan, redirectUrl) => {
    return `/checkout?plan=${plan}&redirect=${redirectUrl}`;
  };
  const formatPrice = (price) => {
    return `€${price}`;
  };

  return (
    <>
      <section className="py-12 sm:py-16 bg-slate-50 animated-bg">
        <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto">
          {/* banner */}
          <div className="mb-8 sm:mb-12">
            {/* Bannière Promotionnelle UCL 2025 */}
            <div className="relative bg-gradient-to-r from-[#0b0c2a] via-[#1a1c4b] to-[#00d4ff] rounded-lg sm:rounded-xl px-4 sm:px-5 py-5 sm:py-6 overflow-hidden shadow-xl max-w-4xl m-auto">
              {/* Background Elements */}
              <div className="absolute top-0 right-0 w-48 h-48 bg-white opacity-5 rounded-full blur-2xl transform translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#00d4ff] opacity-10 rounded-full blur-xl transform -translate-x-1/2 translate-y-1/2"></div>

              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4">
                {/* Left Content */}
                <div className="flex-1 text-center md:text-left space-y-2 mb-8 md:mb-0">
                  <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/30 rounded-full px-2 py-0.5 backdrop-blur-sm">
                    <Trophy className="w-3 h-3 text-[#00d4ff]" />
                    <span className="text-white text-[10px] sm:text-xs font-bold tracking-wider">UCL 2026 • TVDIAMO</span>
                  </div>

                  <h2 className="text-lg sm:text-xl md:text-2xl font-black text-white leading-tight">
                    La Ligue des Champions en 4K
                  </h2>

                  <p className="text-blue-100 text-xs sm:text-sm font-medium max-w-lg">
                    Vivez la magie de l'Europe. Tous les matchs en direct, sans coupure.
                  </p>

                  <div className="flex flex-wrap justify-center md:justify-start gap-2 pt-1">
                    <div className="flex items-center gap-1.5 bg-[#000000]/30 rounded-md px-2 py-1">
                      <Tv className="w-3 h-3 text-[#00d4ff]" />
                      <span className="text-white text-[10px] sm:text-xs font-semibold">Matchs & replays</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-[#000000]/30 rounded-md px-2 py-1">
                      <Monitor className="w-3 h-3 text-[#00d4ff]" />
                      <span className="text-white text-[10px] sm:text-xs font-semibold">4K HDR</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-[#000000]/30 rounded-md px-2 py-1">
                      <Zap className="w-3 h-3 text-yellow-400" />
                      <span className="text-white text-[10px] sm:text-xs font-semibold">Instantané</span>
                    </div>
                  </div>

                  <div className="pt-2 block items-center gap-2 md:flex">
                    <button
                      onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                      className="bg-[#00d4ff] hover:brightness-110 text-[#0b0c2a] font-black py-2 px-5 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1.5 mx-auto md:mx-0 text-sm"
                    >
                      <Gift className="w-4 h-4" />
                      Voir les Offres
                    </button>
                    <span className="text-white/80 text-xs">
                      Satisfait ou Remboursé
                    </span>
                  </div>
                </div>

                {/* Right Visuals (Cards Composition) */}
                <div className="relative w-full md:w-auto flex justify-center md:justify-end mt-4 md:mt-0">
                  <div className="flex items-start gap-1">
                    {/* Image 1 */}
                    <div className="w-20 h-28 sm:w-28 sm:h-36 rounded-lg overflow-hidden border-2 border-white/10 shadow-lg hover:scale-105 transition-all duration-300">
                      <img src={ucl1} alt="Champions League" className="w-full h-full object-cover" />
                    </div>

                    {/* Image 2 */}
                    <div className="w-24 h-32 sm:w-40 sm:h-52 rounded-lg overflow-hidden border-2 border-white/20 shadow-2xl hover:scale-105 transition-all duration-300">
                      <img src={ucl2} alt="UCL Trophy" className="w-full h-full object-cover" />
                    </div>

                    {/* Image 3 */}
                    <div className="w-20 h-28 sm:w-28 sm:h-36 rounded-lg overflow-hidden border-2 border-white/10 shadow-lg hover:scale-105 transition-all duration-300">
                      <img src={ucl3} alt="Football Match" className="w-full h-full object-cover" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl mx-auto font-black mb-3 sm:mb-4 max-w-3xl leading-snug text-center">
              Découvrez Nos{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff]">
                Meilleurs Forfaits
              </span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Options flexibles pour tous
            </p>
          </div>
          {/*IBO*/}
          <div className="mb-8 sm:mb-12">
            {/* Bannière Promotionnelle */}
            <div className="relative bg-gradient-to-r from-[#00d4ff] to-[#090979] rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
              {/* Éléments décoratifs de fond */}
              <div className="absolute left-0 top-0 w-12 sm:w-16 h-full bg-[#04003b] opacity-80 rounded-r-xl sm:rounded-r-2xl transform -skew-x-12"></div>
              <div className="absolute left-3 sm:left-4 top-0 w-8 sm:w-12 h-full bg-[#030024] opacity-60 rounded-r-xl sm:rounded-r-2xl transform -skew-x-12"></div>

              {/* Contenu principal */}
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
                {/* Texte principal */}
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-white mb-2">
                    Activation Gratuite Pour L'App IBO Pro Player
                  </h3>
                  <p className="text-blue-100 text-base sm:text-lg font-semibold">
                    Offre spéciale limitée
                  </p>
                </div>

                {/* Icône cadeau moderne */}
                <div className="flex-shrink-0">
                  <div className="relative group">
                    {/* Cercle de fond avec dégradé bleu */}
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#00d4ff] to-[#090979] rounded-full shadow-2xl flex items-center justify-center transform group-hover:scale-110 transition-all duration-300">
                      {/* Icône Gift avec couleurs compatibles */}
                      <Gift className="w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-lg" />
                    </div>

                    {/* Effet de brillance animé */}
                    <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full opacity-90 animate-pulse flex items-center justify-center">
                      <Diamond className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#00d4ff]" />
                    </div>

                    {/* Particules flottantes */}
                    <div className="absolute -top-2 -left-2 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#00d4ff] rounded-full opacity-60 animate-bounce"></div>
                    <div
                      className="absolute -bottom-1 -right-2 w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white rounded-full opacity-80 animate-bounce"
                      style={{ animationDelay: "0.5s" }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Effet de particules flottantes */}
              <div className="absolute top-3 sm:top-4 right-6 sm:right-8 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full opacity-60 animate-bounce"></div>
              <div
                className="absolute top-6 sm:top-8 right-10 sm:right-12 w-1 h-1 bg-white rounded-full opacity-40 animate-bounce"
                style={{ animationDelay: "0.5s" }}
              ></div>
              <div
                className="absolute bottom-4 sm:bottom-6 right-4 sm:right-6 w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white rounded-full opacity-50 animate-bounce"
                style={{ animationDelay: "1s" }}
              ></div>
            </div>
          </div>

          {/* Plan type toggle: Standard | Multi-Screen */}
          <div className="flex justify-center mb-8 sm:mb-10" id="pricing">
            <div
              className="inline-flex rounded-full bg-white p-1 shadow-lg border border-gray-200"
              role="tablist"
              aria-label="Type de forfait"
            >
              <button
                type="button"
                role="tab"
                aria-selected={isStandard}
                onClick={() => setPlanMode("standard")}
                className={`relative px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-bold text-sm sm:text-base transition-all duration-300 ${
                  isStandard
                    ? "bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] text-white shadow-md"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                Standard
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!isStandard}
                onClick={() => setPlanMode("multiScreen")}
                className={`relative px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-bold text-sm sm:text-base transition-all duration-300 ${
                  !isStandard
                    ? "bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] text-white shadow-md"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                Multi-Screen
              </button>
            </div>
          </div>

          {/* Pricing Cards — dynamic grid with fade/slide on switch */}
          <div
            key={planMode}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 pricing-grid-animate"
          >
            {activePlans.map((plan) => (
              <PricingPlanCard
                key={plan.id}
                plan={plan}
                isStandard={isStandard}
                formatPrice={formatPrice}
                createCheckoutUrl={createCheckoutUrl}
                onTrackAddToCart={(value) => {
                  if (typeof window !== "undefined" && window.fbq) {
                    window.fbq("track", "AddToCart", { value, currency: "USD" });
                  }
                }}
              />
            ))}
          </div>

          {/* LIFETIME PLAN */}
          <div className="flex justify-center mt-6 sm:mt-8 lg:mt-12">
            <div className="w-full lg:w-1/3 lg:max-w-md">
              <div className="relative bg-gradient-to-b from-[#04003b] to-[#090979] rounded-2xl p-1 shadow-2xl overflow-hidden group transition-all duration-500">
                {/* Animated Border Gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#00d4ff] via-[#090979] to-[#00d4ff] opacity-50 group-hover:opacity-100 transition-opacity duration-500 animate-spin-slow"></div>

                <div className="relative bg-[#04003b] rounded-xl p-8 h-full flex flex-col items-center text-center">
                  {/* Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="bg-gradient-to-r from-[#00d4ff] to-[#090979] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-pulse">
                      OFFRE LIMITÉE
                    </span>
                  </div>

                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#00d4ff] to-[#090979] mb-4 shadow-lg group-hover:scale-110 transition-transform duration-500">
                      <Diamond className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-black text-white mb-2">
                      {PRICING_DATA.lifetime.name}
                    </h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl sm:text-5xl font-black text-white">
                        {PRICING_DATA.lifetime.price}€
                      </span>
                      <span className="text-blue-200 font-medium">/ unique</span>
                    </div>
                  </div>

                  <div className="space-y-4 mb-8 w-full text-left">
                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-green-500/20 rounded-full">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      </div>
                      <span className="text-white font-medium">Paiement unique (Pas d'abonnement)</span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-blue-500/20 rounded-full">
                        <Monitor className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="text-white font-medium">2 Écrans simultanés</span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-purple-500/20 rounded-full">
                        <Gift className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="text-white font-medium">Accès complet au Forfait PREMIUM</span>
                    </div>

                    <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="p-1.5 bg-orange-500/20 rounded-full">
                        <Shield className="w-5 h-5 text-orange-400" />
                      </div>
                      <span className="text-white font-medium">30 Jours Garantie De Remboursement</span>
                    </div>
                  </div>

                  <button
                    className="w-full bg-gradient-to-r from-[#00d4ff] to-[#090979] hover:from-[#090979] hover:to-[#00d4ff] text-white font-black py-4 px-8 rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                    onClick={() => {
                      if (window.fbq) {
                        window.fbq('track', 'AddToCart', { value: 139.00, currency: 'USD' });
                      }
                      window.open(
                        createCheckoutUrl('lifetime', PRICING_DATA.lifetime.link),
                        "_blank"
                      )
                    }}
                  >
                    <Gift className="w-5 h-5" />
                    COMMANDER MAINTENANT
                  </button>

                  <p className="mt-4 text-xs text-blue-200 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Paiement 100% Sécurisé
                  </p>
                  {/* Paiement Sécurisé Garanti */}
                  <div className="mt-2 sm:mt-4">
                    <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mt-4 p-3 bg-white rounded-xl w-full">
                      <img src={Google} alt="Google Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                      <img src={cb} alt="CB" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                      <img src={visa} alt="Visa" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                      <img src={apple} alt="Apple Pay" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                      <img src={discover} alt="Discover" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                      <img src={mastercard} alt="Mastercard" className="footer-logo w-10 h-auto drop-shadow-md drop-shadow-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="mt-12 sm:mt-16 text-center">
            <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 max-w-4xl mx-auto ">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
                <div className="text-center md:text-left">
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                    <span className="text-[#00d4ff] font-semibold">
                      Si vous voulez un abonnement personnalisé
                    </span>
                    <span className="text-[#090979] font-semibold">
                      {" "}
                      (pays, langue, écran),
                    </span>
                    <br />
                    <span className="text-[#090979] font-semibold">
                      contactez-nous via WhatsApp ou e-mail.
                    </span>
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 sm:gap-3 bg-[#25d366] hover:bg-[#128C7E] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm sm:text-base min-h-[44px]"
                  >
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
